import { useRef, useState } from "react";
import { AetherBackdrop } from "./components/AetherBackdrop";
import { Controls } from "./components/Controls";
import { Details, TABS } from "./components/Details";
import { Hero } from "./components/Hero";
import { Icon } from "./components/Icon";
import { Inspector } from "./components/Inspector";
import { LairMap } from "./components/LairMap";
import { LineageRail } from "./components/LineageRail";
import { IntroSequence } from "./components/IntroSequence";
import { FACETS } from "./generator";
import { createDossier, hasEditRights, rememberEditToken, updateDossier } from "./lib/api";
import {
  exportJson,
  exportMarkdown,
  exportPng,
  exportSvg
} from "./lib/export";
import { appPath, appUrl, assetPath } from "./lib/urls";
import { useWorkbench } from "./store/useWorkbench";
import type { AgeClass, Facet, GeneratorOptions } from "./types";
import "./styles/app.css";

function randomSeed(): string {
  const words = [
    "ember",
    "crown",
    "velvet",
    "storm",
    "hollow",
    "sulfur",
    "oracle",
    "bronze",
    "thorn",
    "tide",
    "winter",
    "oath"
  ];
  const values = crypto.getRandomValues(new Uint16Array(3));
  return `${words[values[0] % words.length]}-${words[values[1] % words.length]}-${String(
    values[2] % 10000
  ).padStart(4, "0")}`;
}

function App() {
  const workbench = useWorkbench();
  const mapRef = useRef<SVGSVGElement>(null);
  const [controlsOpen, setControlsOpen] = useState(false);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [darkMode, setDarkMode] = useState(true);

  const {
    dossier,
    options,
    setOptions,
    commit,
    generateAll,
    switchLineage,
    rerollFacet,
    toggleLock,
    selectedRoom,
    selectedRoomId,
    setSelectedRoomId,
    selectedFaction,
    selectedFactionId,
    setSelectedFactionId,
    activeTab,
    setActiveTab,
    status,
    setStatus,
    issues,
    undo,
    redo,
    canUndo,
    canRedo,
    replaceDossier
  } = workbench;

  const setSelectedRoom = (id: string) => {
    setSelectedRoomId(id);
    setSelectedFactionId(null);
    setInspectorOpen(true);
  };

  const randomizeAll = () => {
    const lineages = [
      "red",
      "blue",
      "green",
      "black",
      "white",
      "gold",
      "silver",
      "bronze",
      "brass",
      "copper"
    ];
    const ages: AgeClass[] = ["wyrmling", "young", "adult", "ancient"];
    const values = crypto.getRandomValues(new Uint8Array(2));
    const next: Partial<GeneratorOptions> = {
      seed: randomSeed(),
      lineage: lineages[values[0] % lineages.length],
      ageClass: ages[values[1] % ages.length]
    };
    generateAll(next);
  };

  const save = async () => {
    setSaving(true);
    setStatus("Saving…");
    try {
      const result =
        dossier.id && hasEditRights()
          ? await updateDossier(dossier)
          : await createDossier(dossier);
      const next = structuredClone(dossier);
      next.id = result.id;
      next.revision = result.revision;
      replaceDossier(next, "Saved to server");
      if (result.editToken) rememberEditToken(result.id, result.editToken);
      if (result.editUrl) {
        const url = new URL(result.editUrl, window.location.origin);
        window.history.replaceState({}, "", `${url.pathname}${url.hash}`);
      }
    } catch (error) {
      setStatus(
        error instanceof Error
          ? `Server save unavailable — local draft remains safe (${error.message})`
          : "Server save unavailable — local draft remains safe"
      );
    } finally {
      setSaving(false);
    }
  };

  const share = async () => {
    try {
      if (!dossier.id) {
        setSaving(true);
        const result = await createDossier(dossier);
        const next = structuredClone(dossier);
        next.id = result.id;
        next.revision = result.revision;
        replaceDossier(next, "Public link created");
        if (result.editToken) rememberEditToken(result.id, result.editToken);
        if (result.editUrl) {
          const editUrl = new URL(result.editUrl, window.location.origin);
          window.history.replaceState({}, "", `${editUrl.pathname}${editUrl.hash}`);
        }
        await navigator.clipboard.writeText(new URL(result.publicUrl, window.location.origin).href);
      } else {
        await navigator.clipboard.writeText(
          appUrl(`d/${encodeURIComponent(dossier.id)}`)
        );
      }
      setStatus("Public read-only link copied");
    } catch {
      const fallback = `${appUrl()}?seed=${encodeURIComponent(
        options.seed
      )}&lineage=${options.lineage}&age=${options.ageClass}&adapter=${options.adapter}`;
      await navigator.clipboard.writeText(fallback);
      setStatus("Reproducible seed link copied");
    } finally {
      setSaving(false);
    }
  };

  const moveRoom = (id: string, x: number, y: number) =>
    commit((draft) => {
      const room = draft.lair.rooms.find((candidate) => candidate.id === id);
      if (room) {
        room.x = x;
        room.y = y;
      }
    }, "Room moved");

  return (
    <div
      className={`app-shell ${darkMode ? "" : "app-shell--parchment"}`}
      style={
        {
          "--accent": dossier.appearance.accent,
          "--accent-soft": dossier.appearance.accentSoft,
          "--texture-obsidian": `url("${assetPath("assets/textures/obsidian-panel.webp")}")`
        } as React.CSSProperties
      }
    >
      <AetherBackdrop
        accent={dossier.appearance.accent}
        accentSoft={dossier.appearance.accentSoft}
        lineage={dossier.dragon.lineage}
      />
      <IntroSequence dossier={dossier} />
      <header className="topbar">
        <button
          type="button"
          className="mobile-menu-button"
          aria-label="Open generator controls"
          onClick={() => setControlsOpen(true)}
        >
          <Icon name="menu" />
        </button>
        <div className="brand" aria-label="Tiamat.nl Dragon Generator">
          <svg viewBox="0 0 50 50" aria-hidden="true">
            <path d="M39 10C23 2 9 11 12 24c2 8 12 7 16 2 5-7-5-12-10-7 2-10 17-9 20 1 4 13-10 25-25 17 12 12 34 4 34-14 0-5-2-10-8-13Z" />
            <path d="m13 36-7 7 10-3m21-29 8-6-3 9" />
          </svg>
          <span>
            <strong>Tiamat.nl</strong>
            <small>Dragon Generator</small>
          </span>
        </div>
        <label className="seed-control">
          <span>Seed</span>
          <input
            aria-label="Generator seed"
            value={options.seed}
            onChange={(event) => setOptions({ ...options, seed: event.target.value })}
            onKeyDown={(event) => {
              if (event.key === "Enter") generateAll();
            }}
          />
          <button
            type="button"
            aria-label="Copy seed"
            onClick={() => {
              void navigator.clipboard.writeText(options.seed);
              setStatus("Seed copied");
            }}
          >
            <Icon name="copy" />
          </button>
          <button
            type="button"
            aria-label="Generate new seed"
            onClick={() => generateAll({ seed: randomSeed() })}
          >
            <Icon name="reroll" />
          </button>
        </label>
        <div className="topbar__actions">
          <button type="button" onClick={() => setActiveTab("export")}>
            <Icon name="export" /> <span>Export</span>
          </button>
          <button type="button" disabled={saving} onClick={() => void share()}>
            <Icon name="share" /> <span>Share</span>
          </button>
          <button type="button" className="topbar__save" disabled={saving} onClick={() => void save()}>
            <Icon name="save" /> <span>{saving ? "Saving…" : "Save"}</span>
          </button>
          <button
            type="button"
            aria-label="Undo"
            title="Undo"
            disabled={!canUndo}
            onClick={undo}
          >
            <Icon name="undo" />
          </button>
          <button
            type="button"
            aria-label="Redo"
            title="Redo"
            disabled={!canRedo}
            onClick={redo}
          >
            <Icon name="redo" />
          </button>
          <button
            type="button"
            aria-label="Toggle parchment theme"
            onClick={() => setDarkMode((value) => !value)}
          >
            <Icon name={darkMode ? "moon" : "sun"} />
          </button>
        </div>
      </header>

      <LineageRail current={options.lineage} onSelect={switchLineage} />

      <main className="workbench">
        <div className={`mobile-drawer-backdrop ${controlsOpen ? "is-open" : ""}`} onClick={() => setControlsOpen(false)} />
        <div className={`controls-drawer ${controlsOpen ? "is-open" : ""}`}>
          <button
            type="button"
            className="drawer-close"
            aria-label="Close generator controls"
            onClick={() => setControlsOpen(false)}
          >
            ×
          </button>
          <Controls
            options={options}
            onOptionsChange={setOptions}
            onGenerate={() => {
              generateAll();
              setControlsOpen(false);
            }}
            onRandomize={() => {
              randomizeAll();
              setControlsOpen(false);
            }}
            onToggleLock={toggleLock}
          />
        </div>

        <div className="primary-workspace">
          <div className="hero-map-row">
            <Hero dossier={dossier} onEdit={commit} />
            <section className="map-panel ornate-panel">
              <div className="ornate-frame" aria-hidden="true">
                <i /><i /><i /><i />
              </div>
              <div className="panel-heading panel-heading--map">
                <span>Lair map</span>
                <small>Drag rooms to edit</small>
              </div>
              <LairMap
                svgRef={mapRef}
                lair={dossier.lair}
                selectedRoomId={selectedRoomId}
                accent={dossier.appearance.accent}
                portrait={dossier.appearance.portrait}
                onSelect={setSelectedRoom}
                onMove={moveRoom}
              />
              <div className="map-resources">
                <span><Icon name="lair" /> {dossier.lair.rooms.length} rooms</span>
                <span><Icon name="hoard" /> {dossier.hoard.totalValue.toLocaleString("en-US")} gp</span>
                <span><Icon name="age" /> {dossier.dragon.ageYears} yrs</span>
                <span><Icon name="spark" /> {dossier.lair.layout ?? `${dossier.regionalEffects.length} effects`}</span>
              </div>
            </section>
          </div>

          <nav className="dossier-tabs" aria-label="Dossier sections">
            {TABS.map(([id, label]) => (
              <button
                type="button"
                className={activeTab === id ? "is-active" : ""}
                aria-current={activeTab === id ? "page" : undefined}
                key={id}
                onClick={() => setActiveTab(id)}
              >
                {label}
              </button>
            ))}
          </nav>

          <Details
          key={activeTab}
            activeTab={activeTab}
            dossier={dossier}
            issues={issues}
            selectedFactionId={selectedFactionId}
            onSelectFaction={(id) => {
              setSelectedFactionId(id);
              setInspectorOpen(true);
            }}
            onSelectRoom={setSelectedRoom}
            onEdit={commit}
            onReroll={rerollFacet}
            onExportJson={() => exportJson(dossier)}
            onExportMarkdown={() => exportMarkdown(dossier)}
            onExportSvg={() => {
              if (mapRef.current) exportSvg(mapRef.current, dossier);
            }}
            onExportPng={() => {
              if (mapRef.current) void exportPng(mapRef.current, dossier);
            }}
            onPrint={() => window.print()}
          />

          <div className="facet-bar" aria-label="Facet reroll controls">
            <span>Reroll</span>
            {FACETS.map((facet: Facet) => (
              <button
                type="button"
                key={facet}
                disabled={options.locks[facet]}
                onClick={() => rerollFacet(facet)}
              >
                <Icon name={options.locks[facet] ? "lock" : "reroll"} /> {facet}
              </button>
            ))}
          </div>
        </div>

        <div className={`inspector-drawer ${inspectorOpen ? "is-open" : ""}`}>
          <button
            type="button"
            className="drawer-close inspector-drawer__close"
            aria-label="Close inspector"
            onClick={() => setInspectorOpen(false)}
          >
            ×
          </button>
          <Inspector
            dossier={dossier}
            room={selectedRoom}
            faction={selectedFaction}
            onEdit={commit}
            onSelectRoom={setSelectedRoom}
            onClearFaction={() => setSelectedFactionId(null)}
          />
        </div>
      </main>
      <footer className="statusbar" aria-live="polite">
        <span>{status}</span>
        <span>
          {issues.filter((issue) => issue.severity === "error").length
            ? `${issues.filter((issue) => issue.severity === "error").length} validation errors`
            : `${issues.length} suggestions`}
        </span>
        <a href={appPath("legal.html")} target="_blank" rel="noreferrer">
          Legal &amp; attribution
        </a>
        <button type="button" onClick={() => setInspectorOpen(true)}>
          Inspector
        </button>
      </footer>
    </div>
  );
}

export default App;
